import { api } from '../api';
import type { Empresa, UsuarioListItem } from '../types';

export interface AltaPlanPayload {
  codigo: string;
  nombre?: string;
  sucursalId?: string;
  categoriaAfiliadoCodigo?: string;
  aaCodigo?: string;
  aa2Codigo?: string;
  esBase?: boolean;
  /** Disponible para la venta: solo estos se le ofrecen al promotor en un padrón. */
  disponibleVenta?: boolean;
}

export interface AltaConfigPayload {
  habilitada?: boolean;
  obraSocialCodigo?: string;
  promotorId?: string;
  firmaSolicitud?: string;
  agenciaEnvio?: string;
  planes?: AltaPlanPayload[];
  // Deprecados (back-compat).
  sucursalId?: string;
  categoriaAfiliadoCodigo?: string;
  aaCodigo?: string;
  aa2Codigo?: string;
}

export interface ConfigSugeridaEmpresa {
  emplId: number;
  identidad: { nombre: string | null; cuit: string | null };
  obraSocialCodigo: string | null;
  promotorId: string | null;
  firmaSolicitud: string | null;
  agenciaEnvio: string;
  categoriaAfiliadoCodigo: string | null;
  aaCodigo: string | null;
  aa2Codigo: string | null;
  planes: Array<{
    codigo: string;
    nombre: string;
    sucursalId: string;
    categoriaAfiliadoCodigo: string;
    aaCodigo: string;
    aa2Codigo: string;
  }>;
  notas: string[];
  /** De dónde salió cada valor sugerido (informativo, no es un problema). */
  procedencia?: string[];
}

export async function getConfigSugeridaEmpresa(emplId: number): Promise<ConfigSugeridaEmpresa> {
  const { data } = await api.get<ConfigSugeridaEmpresa>(
    `/admin/oracle/empresas/${emplId}/config-sugerida`,
  );
  return data;
}

/**
 * Config de la DDJJ de la empresa. Se mandan las preguntas APAGADAS: lo que no
 * está en la lista se pregunta. Así una pregunta nueva del catálogo se le hace
 * a todas las empresas sin tocar la config de cada una.
 */
export interface DdjjConfigPayload {
  preguntasExcluidas?: string[];
  /**
   * Cuestionario propio (importado de Binary o de un Excel). REEMPLAZA al
   * catálogo del micrositio. `[]` = volver al catálogo. Si no viene, el server
   * conserva el que la empresa ya tiene (mergea campo por campo).
   */
  preguntas?: DdjjPreguntaPropia[];
  origen?: 'catalogo' | 'binary' | 'excel';
}

/** Una pregunta de un cuestionario propio, en el formato normalizado del server. */
export interface DdjjPreguntaPropia {
  codigo: string;
  seccion: number;
  seccionTitulo: string;
  pregunta: string;
  tipo: string;
  opciones?: string[];
  textLabel?: string;
  textLabel2?: string;
  mimeAceptados?: string[];
  tamMaxMB?: number;
  soloTitular?: boolean;
  heredaAFamiliares?: boolean;
  opcional?: boolean;
  dependeDe?: { codigo: string; valor: string };
}

/** Un cuestionario de DDJJ configurado en Binary (`TMK.ONB_CFG`, origen + tipo). */
export interface CuestionarioDdjjBinary {
  id: number;
  mainId: string;
  onbType: string;
  descripcion: string;
  preguntas: number;
}

/** El del portal de socios: la DDJJ "de Binary" por defecto. */
export const CUESTIONARIO_DDJJ_PORTAL = { mainId: 'PS', onbType: 'ALTA' } as const;

export async function listarCuestionariosDdjjBinary(): Promise<CuestionarioDdjjBinary[]> {
  const { data } = await api.get<{ cuestionarios: CuestionarioDdjjBinary[] }>(
    '/admin/oracle/ddjj-cuestionarios',
  );
  return data.cuestionarios;
}

export interface DdjjDesdeBinary {
  cuestionario: { mainId: string; onbType: string };
  total: number;
  preguntas: DdjjPreguntaPropia[];
  /** Conceptos del cuestionario que no entran como pregunta (80, 90, dados de baja). */
  omitidos: Array<{ codigo: string; descripcion: string; motivo: string }>;
}

export async function getDdjjDesdeBinary(cuestionario?: {
  mainId: string;
  onbType: string;
}): Promise<DdjjDesdeBinary> {
  const { data } = await api.get<DdjjDesdeBinary>('/admin/oracle/ddjj-conceptos', {
    params: cuestionario ? { origen: cuestionario.mainId, tipo: cuestionario.onbType } : {},
  });
  return data;
}

/** Fila cruda del Excel del cuestionario (columnas de la plantilla, sin tipar). */
export type FilaDdjjExcel = Record<string, unknown> & { fila?: number };

export interface ValidacionDdjjExcel {
  preguntas: DdjjPreguntaPropia[];
  errores: Array<{ fila: number; codigo: string | null; campo: string; motivo: string }>;
  total: number;
}

/** Valida un cuestionario cargado por Excel SIN guardarlo. */
export async function validarDdjjExcel(filas: FilaDdjjExcel[]): Promise<ValidacionDdjjExcel> {
  const { data } = await api.post<ValidacionDdjjExcel>('/empresas/ddjj/validar', { filas });
  return data;
}

async function descargarXlsx(path: string, filename: string): Promise<void> {
  const res = await api.get(path, { responseType: 'blob' });
  const blob = new Blob([res.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** Plantilla vacía del cuestionario (un ejemplo de cada tipo + instructivo). */
export function descargarPlantillaDdjj(): Promise<void> {
  return descargarXlsx('/empresas/ddjj/plantilla', 'ddjj-plantilla.xlsx');
}

/** El catálogo del micrositio (el de MS) en el formato de la plantilla, para editarlo. */
export function descargarCatalogoDdjjXlsx(): Promise<void> {
  return descargarXlsx('/empresas/ddjj/catalogo-propio.xlsx', 'ddjj-catalogo-actual.xlsx');
}

export interface PreguntaCatalogoDdjj {
  codigo: string;
  seccion: number;
  seccionTitulo: string;
  pregunta: string;
  tipo: string;
  soloTitular: boolean;
  opcional: boolean;
  /** Si está seteado, apagar esa pregunta apaga también a ésta. */
  dependeDe?: string;
}

export async function getCatalogoDdjj(): Promise<PreguntaCatalogoDdjj[]> {
  const { data } = await api.get<{ preguntas: PreguntaCatalogoDdjj[] }>('/empresas/catalogo-ddjj');
  return data.preguntas;
}

export interface CreateEmpresaPayload {
  cuit: string;
  razonSocial: string;
  nombreComercial: string;
  contactoNombre: string;
  contactoEmail: string;
  contactoTelefono?: string;
  oracleEmplId?: number;
  planBaseCodigo?: string;
  planSuperadorCodigo?: string;
  altaConfig?: AltaConfigPayload;
  ddjjConfig?: DdjjConfigPayload;
  /** Sin asistente del titular: los legajos pasan directo al alta al validar el padrón. */
  sinWizard?: boolean;
}

export type UpdateEmpresaPayload = Partial<CreateEmpresaPayload> & { activa?: boolean };

export interface PlanOracle {
  codigo: string;
  nombre: string;
  descripcion?: string;
}

export interface PlanContratadoOracle extends PlanOracle {
  planCodigo: string;
  sucEmpCodigo?: string;
  fechaDesde: string;
  fechaHasta?: string;
}

export async function listarPlanesOracle(search?: string): Promise<PlanOracle[]> {
  const { data } = await api.get<{ planes: PlanOracle[] }>('/admin/oracle/planes', {
    params: { search },
  });
  return data.planes;
}

export async function listarPlanesEmpresaOracle(emplId: number): Promise<PlanContratadoOracle[]> {
  const { data } = await api.get<{ emplId: number; planes: PlanContratadoOracle[] }>(
    `/admin/oracle/empresas/${emplId}/planes`,
  );
  return data.planes;
}

export interface EmpresaOracleCheck {
  emplId: number;
  existe: boolean;
  planesVigentes: number;
  afiliadosActivos: number;
}

export async function verificarEmpresaOracle(emplId: number): Promise<EmpresaOracleCheck> {
  const { data } = await api.get<EmpresaOracleCheck>(`/admin/oracle/empresas/${emplId}`);
  return data;
}

export async function listEmpresas(search?: string): Promise<Empresa[]> {
  const { data } = await api.get<Empresa[]>('/empresas', { params: { search } });
  return data;
}

export async function getEmpresa(id: string): Promise<Empresa> {
  const { data } = await api.get<Empresa>(`/empresas/${id}`);
  return data;
}

export async function createEmpresa(payload: CreateEmpresaPayload): Promise<Empresa> {
  const { data } = await api.post<Empresa>('/empresas', payload);
  return data;
}

export async function updateEmpresa(id: string, payload: UpdateEmpresaPayload): Promise<Empresa> {
  const { data } = await api.put<Empresa>(`/empresas/${id}`, payload);
  return data;
}

export async function deleteEmpresa(id: string): Promise<void> {
  await api.delete(`/empresas/${id}`);
}

export async function listUsuariosDeEmpresa(empresaId: string): Promise<UsuarioListItem[]> {
  const { data } = await api.get<UsuarioListItem[]>(`/empresas/${empresaId}/usuarios`);
  return data;
}
